import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import jsQR from "jsqr";
import { Loader2, X, ScanLine, Camera, RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useOverlayLock } from "@/contexts/UiOverlayContext";
import { useScanTradeQR, normalisePin, tradeErrorKey } from "@/lib/trade";
import { useI18n } from "@/lib/I18nContext";
import TradeMirror from "@/components/TradeMirror";

const PIN_LENGTH = 6;
const SCAN_INTERVAL_MS = 200;

// Backend error keys that surface as a dedicated error modal (Phase 18 A7).
// Phase 19b MODAL-ALL bundle: `pin_not_found_or_expired` promoted into the
// modal path so the user gets the same clear treatment for expired PINs.
// Everything else stays in the inline banner.
const TOKEN_ERROR_KEYS = new Set([
  "token_expired",
  "token_invalid",
  "token_wrong_kind",
  "pin_not_found_or_expired",
]);

/**
 * Phase 17b + Phase 18 — Scanner side.
 *
 * Two-tab UX: [Camera Scanner] (default active) + [Manual PIN Input]. On
 * mount the camera tab requests permission; on rejection or unsupported
 * browser, the camera tab is disabled and the modal auto-flips to the
 * PIN tab. Persistent disclaimer at the top of the body explains what the
 * camera is used for.
 *
 * Token-shaped backend errors (expired / invalid / wrong-kind) surface as
 * a dedicated error modal (with Retry + Close CTAs) on top of the inline
 * banner so users don't miss them. All errors also dispatch the ASL
 * feedback event; the border only renders when data-a11y-mode="asl".
 *
 * All CSS motion is transition-only (Tailwind classes) — no explicit
 * `animation:` rules — so ASL motion-reduce sweeps everything cleanly.
 */
export default function TradeScanModal({ open, onOpenChange }) {
  useOverlayLock(open);
  const { t } = useI18n();
  const scan = useScanTradeQR();

  const [cameraError, setCameraError] = useState(null); // "denied" | "unsupported" | null
  const [activeTab, setActiveTab] = useState("camera"); // "camera" | "pin"
  const [matchedSession, setMatchedSession] = useState(null);
  const [errKey, setErrKey] = useState(null);
  const [pinDigits, setPinDigits] = useState(Array(PIN_LENGTH).fill(""));
  const pinInputRefs = useRef([]);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanLockedRef = useRef(false);

  const dispatchAsl = useCallback((type) => {
    window.dispatchEvent(
      new CustomEvent("fm:asl_feedback", { detail: { type } })
    );
  }, []);

  const submitToken = useCallback(
    (token) => {
      if (scanLockedRef.current || scan.isPending) return;
      scanLockedRef.current = true;
      setErrKey(null);
      scan.mutate(
        { token },
        {
          onSuccess: (data) => {
            setMatchedSession(data);
            dispatchAsl("success");
          },
          onError: (err) => {
            scanLockedRef.current = false;
            setErrKey(tradeErrorKey(err));
            dispatchAsl("error");
          },
        }
      );
    },
    [scan, dispatchAsl]
  );

  const submitPin = useCallback(
    (rawPin) => {
      const pin = normalisePin(rawPin);
      if (pin.length !== PIN_LENGTH) return;
      if (scanLockedRef.current || scan.isPending) return;
      scanLockedRef.current = true;
      setErrKey(null);
      scan.mutate(
        { pin },
        {
          onSuccess: (data) => {
            setMatchedSession(data);
            dispatchAsl("success");
          },
          onError: (err) => {
            scanLockedRef.current = false;
            setErrKey(tradeErrorKey(err));
            dispatchAsl("error");
          },
        }
      );
    },
    [scan, dispatchAsl]
  );

  // Reset on close.
  useEffect(() => {
    if (open) return;
    scanLockedRef.current = false;
    setCameraError(null);
    setActiveTab("camera");
    setMatchedSession(null);
    setErrKey(null);
    setPinDigits(Array(PIN_LENGTH).fill(""));
  }, [open]);

  // Camera lifecycle — open when the modal is open, camera tab is active,
  // and we haven't matched yet.
  const cameraShouldRun =
    open && !matchedSession && activeTab === "camera" && !cameraError;

  useEffect(() => {
    if (!cameraShouldRun) return;
    let cancelled = false;

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia
    ) {
      setCameraError("unsupported");
      setActiveTab("pin");
      return;
    }

    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          v.setAttribute("playsinline", "true");
          v.muted = true;
          v.play().catch(() => {
            /* autoplay may fail in test envs; user still sees the frame */
          });
        }
        setCameraError(null);
      })
      .catch(() => {
        // Denied or otherwise unusable — auto-switch to PIN tab (A4).
        setCameraError("denied");
        setActiveTab("pin");
      });

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((tr) => tr.stop());
        streamRef.current = null;
      }
    };
  }, [cameraShouldRun]);

  // Scan loop — 200 ms tick when video is playing.
  useEffect(() => {
    if (!cameraShouldRun) return;
    const id = setInterval(() => {
      if (scanLockedRef.current) return;
      try {
        const v = videoRef.current;
        const canvas = canvasRef.current;
        if (!v || !canvas) return;
        if (v.readyState !== 4 || !v.videoWidth) return;
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imgData.data, imgData.width, imgData.height, {
          inversionAttempts: "dontInvert",
        });
        if (code && code.data) {
          // Any garbage decode is still forwarded — backend will reject
          // with token_invalid and the error modal surfaces it. No crash
          // path (A7 defensive layer).
          submitToken(code.data);
        }
      } catch (_e) {
        // Cross-origin taint (extremely rare with getUserMedia) or any
        // other DOM error — swallow so the tick keeps ticking. Nothing
        // to display; failed frame is silent.
      }
    }, SCAN_INTERVAL_MS);
    return () => clearInterval(id);
  }, [cameraShouldRun, submitToken]);

  // ── PIN input helpers ─────────────────────────────────────
  function focusBox(i) {
    const ref = pinInputRefs.current[i];
    if (ref) ref.focus();
  }

  function handlePinChange(i, val) {
    const digit = (val || "").replace(/\D/g, "").slice(0, 1);
    setPinDigits((prev) => {
      const next = [...prev];
      next[i] = digit;
      if (digit && i < PIN_LENGTH - 1) {
        setTimeout(() => focusBox(i + 1), 0);
      }
      if (digit && i === PIN_LENGTH - 1) {
        const full = next.join("");
        if (full.length === PIN_LENGTH) {
          setTimeout(() => submitPin(full), 0);
        }
      }
      return next;
    });
    if (errKey) setErrKey(null);
  }

  function handlePinKeyDown(i, e) {
    if (e.key === "Backspace") {
      if (pinDigits[i]) return;
      if (i > 0) {
        e.preventDefault();
        setPinDigits((prev) => {
          const next = [...prev];
          next[i - 1] = "";
          return next;
        });
        focusBox(i - 1);
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === "ArrowRight" && i < PIN_LENGTH - 1) {
      e.preventDefault();
      focusBox(i + 1);
    } else if (e.key === "Enter") {
      const full = pinDigits.join("");
      if (full.length === PIN_LENGTH) submitPin(full);
    }
  }

  function handlePinPaste(e) {
    const raw = (e.clipboardData || window.clipboardData).getData("text");
    const digits = normalisePin(raw).slice(0, PIN_LENGTH);
    if (!digits) return;
    e.preventDefault();
    const next = Array(PIN_LENGTH).fill("");
    for (let i = 0; i < digits.length; i++) next[i] = digits[i];
    setPinDigits(next);
    if (digits.length === PIN_LENGTH) {
      setTimeout(() => submitPin(digits), 0);
    } else {
      focusBox(digits.length);
    }
  }

  const pinValue = useMemo(() => pinDigits.join(""), [pinDigits]);
  const pinComplete = pinValue.length === PIN_LENGTH;

  // ── Error modal gating ─────────────────────────────────────
  const showErrorModal = errKey && TOKEN_ERROR_KEYS.has(errKey);
  const cameraTabDisabled = !!cameraError;

  // Handler for the error modal Retry button — clear error, switch back
  // to camera if possible.
  const handleErrorRetry = useCallback(() => {
    setErrKey(null);
    setPinDigits(Array(PIN_LENGTH).fill(""));
    if (!cameraTabDisabled) {
      setActiveTab("camera");
    }
  }, [cameraTabDisabled]);

  const handleErrorDismiss = useCallback(() => {
    setErrKey(null);
    onOpenChange(false);
  }, [onOpenChange]);

  // ── Render ────────────────────────────────────────────────
  if (matchedSession) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) onOpenChange(false);
        }}
      >
        <DialogContent
          data-testid="trade-scan-modal-matched"
          className="p-0 border-none bg-transparent max-w-none w-screen h-screen sm:rounded-none [&>button:last-child]:hidden"
        >
          <TradeMirror
            perspective="scanner"
            session={matchedSession}
            onClose={() => onOpenChange(false)}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) onOpenChange(false);
        }}
      >
        <DialogContent
          data-testid="trade-scan-modal"
          className="bg-[#0A0A0A] border-white/10 text-white max-w-md p-0 overflow-hidden [&>button:last-child]:hidden"
        >
          <DialogHeader className="px-6 pt-6 pb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-[#E5B83A]/15 border border-[#E5B83A]/30 flex items-center justify-center">
                <ScanLine className="h-4 w-4 text-[#E5B83A]" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="font-display text-lg font-bold text-white truncate">
                  {t("trade.scan_title")}
                </DialogTitle>
                <DialogDescription className="text-[#A1A1AA] font-body text-xs">
                  {t("trade.scan_subtitle")}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 pb-4 space-y-4">
            {/* Phase 18 A3 — persistent camera disclaimer at the top. */}
            <div
              role="note"
              data-testid="trade-scan-disclaimer"
              className="rounded-md border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm text-amber-100"
            >
              {t("trade.scan_camera_disclaimer")}
            </div>

            {/* Optional camera-denied validation message below disclaimer. */}
            {cameraError ? (
              <p
                data-testid="trade-scan-denied-message"
                className="text-xs text-[#FF6B6B] font-body px-1"
              >
                {t("trade.camera_denied_message")}
              </p>
            ) : null}

            {/* Phase 18 A4 — Tab strip. WCAG-compliant semantics. */}
            <div
              role="tablist"
              aria-label={t("trade.scan_title")}
              data-testid="trade-scan-tablist"
              className="flex items-center gap-2 border border-white/10 rounded-md p-1 bg-black/30"
            >
              <TabButton
                testId="trade-scan-tab-camera"
                active={activeTab === "camera"}
                disabled={cameraTabDisabled}
                controls="trade-scan-panel-camera"
                onClick={() => setActiveTab("camera")}
              >
                <Camera className="h-4 w-4" />
                {t("trade.tab_camera")}
              </TabButton>
              <TabButton
                testId="trade-scan-tab-pin"
                active={activeTab === "pin"}
                controls="trade-scan-panel-pin"
                onClick={() => setActiveTab("pin")}
              >
                <ScanLine className="h-4 w-4" />
                {t("trade.tab_pin")}
              </TabButton>
            </div>

            {/* ── Camera panel ─────────────────────────────── */}
            <div
              id="trade-scan-panel-camera"
              role="tabpanel"
              aria-labelledby="trade-scan-tab-camera"
              hidden={activeTab !== "camera"}
              data-testid="trade-scan-panel-camera"
            >
              <div
                data-testid="trade-scan-video-panel"
                className="relative rounded-xl overflow-hidden bg-black border border-white/10 aspect-square flex items-center justify-center"
              >
                {cameraError ? (
                  <CameraFallback
                    error={cameraError}
                    messageDenied={t("trade.camera_error")}
                    messageUnsupported={t("trade.camera_unsupported")}
                  />
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      data-testid="trade-scan-video"
                      className="w-full h-full object-cover"
                      playsInline
                      muted
                      autoPlay
                    />
                    {scan.isPending ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                        <Loader2 className="h-8 w-8 text-[#E5B83A] animate-spin" />
                      </div>
                    ) : (
                      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                        <div className="w-48 h-48 border-2 border-[#E5B83A] rounded-md shadow-[0_0_24px_rgba(229,184,58,0.35)]" />
                      </div>
                    )}
                  </>
                )}
                <canvas ref={canvasRef} className="hidden" aria-hidden="true" />
              </div>
            </div>

            {/* ── PIN panel ────────────────────────────────── */}
            <div
              id="trade-scan-panel-pin"
              role="tabpanel"
              aria-labelledby="trade-scan-tab-pin"
              hidden={activeTab !== "pin"}
              data-testid="trade-scan-panel-pin"
              className="space-y-2"
            >
              <p
                data-testid="trade-scan-pin-label"
                className="font-body text-[10px] uppercase tracking-wider text-[#A1A1AA] text-center"
              >
                {t("trade.enter_pin")}
              </p>
              <div
                data-testid="trade-scan-pin-row"
                className="flex items-center justify-center gap-2"
                onPaste={handlePinPaste}
              >
                {pinDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (pinInputRefs.current[i] = el)}
                    data-testid={`trade-scan-pin-box-${i}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handlePinChange(i, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(i, e)}
                    onFocus={(e) => e.target.select()}
                    className="w-11 h-14 rounded-md border border-white/15 bg-black/60 text-center font-mono font-bold text-white text-2xl focus:border-[#E5B83A] focus:outline-none focus:ring-1 focus:ring-[#E5B83A]"
                  />
                ))}
              </div>
              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  data-testid="trade-scan-pin-submit"
                  onClick={() => submitPin(pinValue)}
                  disabled={!pinComplete || scan.isPending}
                  style={{ minWidth: "44px", minHeight: "44px" }}
                  className="inline-flex items-center gap-2 px-4 rounded-md bg-[#E5B83A] text-black font-body font-semibold text-xs hover:bg-[#D4AF37] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {scan.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Camera className="h-3.5 w-3.5" />
                  )}
                  {t("trade.pin_submit")}
                </button>
              </div>
            </div>

            {/* Inline error banner — for non-token errors */}
            {errKey && !TOKEN_ERROR_KEYS.has(errKey) ? (
              <div
                data-testid="trade-scan-error"
                className="rounded-md border border-[#FF6B6B]/30 bg-[#FF6B6B]/10 px-3 py-2 flex items-center justify-between gap-2"
              >
                <p className="font-body text-xs text-[#FF6B6B] leading-snug">
                  {t(`trade.err.${errKey}`)}
                </p>
                <button
                  type="button"
                  data-testid="trade-scan-error-clear"
                  onClick={() => {
                    setErrKey(null);
                    setPinDigits(Array(PIN_LENGTH).fill(""));
                    focusBox(0);
                  }}
                  className="shrink-0 inline-flex items-center gap-1 h-7 px-2 rounded-md border border-white/10 text-white font-body text-[11px] hover:bg-white/5"
                >
                  <RefreshCw className="h-3 w-3" />
                  {t("buttons.retry")}
                </button>
              </div>
            ) : null}
          </div>

          <div className="px-6 pb-6 pt-2 border-t border-white/5 flex items-center justify-end gap-2">
            <button
              type="button"
              data-testid="trade-scan-close"
              onClick={() => onOpenChange(false)}
              style={{ minWidth: "44px", minHeight: "44px" }}
              className="inline-flex items-center gap-2 px-4 rounded-md border border-white/10 text-white font-body text-sm hover:bg-white/5"
            >
              <X className="h-3.5 w-3.5" />
              {t("buttons.close")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Phase 18 A7 — Token error modal (JWT expired / invalid / wrong-kind). */}
      <Dialog
        open={!!showErrorModal}
        onOpenChange={(next) => {
          if (!next) handleErrorDismiss();
        }}
      >
        <DialogContent
          data-testid="trade-scan-error-modal"
          className="bg-[#0A0A0A] border-[#FF6B6B]/40 text-white max-w-md"
        >
          <DialogHeader>
            <DialogTitle
              data-testid="trade-scan-error-modal-title"
              className="font-display text-lg font-bold text-[#FF6B6B]"
            >
              {t("trade.error_modal_title")}
            </DialogTitle>
            <DialogDescription
              data-testid="trade-scan-error-modal-body"
              className="text-[#E5E5E5] font-body text-sm pt-1"
            >
              {errKey ? t(`trade.err.${errKey}`) : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2 pt-4">
            <button
              type="button"
              data-testid="trade-scan-error-modal-retry"
              onClick={handleErrorRetry}
              style={{ minWidth: "44px", minHeight: "44px" }}
              className="inline-flex items-center gap-2 px-4 rounded-md bg-[#E5B83A] text-black font-body font-semibold text-sm hover:bg-[#D4AF37]"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {t("buttons.retry")}
            </button>
            <button
              type="button"
              data-testid="trade-scan-error-modal-close"
              onClick={handleErrorDismiss}
              style={{ minWidth: "44px", minHeight: "44px" }}
              className="inline-flex items-center gap-2 px-4 rounded-md border border-white/10 text-white font-body text-sm hover:bg-white/5"
            >
              <X className="h-3.5 w-3.5" />
              {t("buttons.close")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TabButton({ testId, active, disabled, controls, onClick, children }) {
  return (
    <button
      type="button"
      role="tab"
      id={testId}
      data-testid={testId}
      aria-selected={active}
      aria-controls={controls}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      onClick={onClick}
      // Phase 18 A4 — min-width/height 44px enforced inline so the rule
      // holds even outside ASL mode. Bumped to 48px because some browsers
      // (headless Chromium at fractional DPR, older iOS Safari) render at
      // sub-pixel scale and report ~43px for a 44px CSS min-height. 48px
      // keeps rendered pixels safely above the WCAG 2.5.5 threshold.
      style={{ minWidth: "44px", minHeight: "48px", height: "48px" }}
      className={
        "flex-1 inline-flex items-center justify-center gap-2 px-4 rounded font-body text-sm transition-colors " +
        (active
          ? "bg-[#E5B83A]/15 text-[#E5B83A] border border-[#E5B83A]/40"
          : disabled
          ? "text-white/30 border border-transparent cursor-not-allowed"
          : "text-white/80 hover:bg-white/5 border border-transparent")
      }
    >
      {children}
    </button>
  );
}

function CameraFallback({ error, messageDenied, messageUnsupported }) {
  const msg = error === "unsupported" ? messageUnsupported : messageDenied;
  return (
    <div
      data-testid="trade-scan-camera-error"
      className="p-6 text-center flex flex-col items-center justify-center h-full"
    >
      <Camera className="h-8 w-8 text-[#A1A1AA] mb-3" />
      <p className="font-body text-sm text-[#A1A1AA] leading-relaxed">{msg}</p>
    </div>
  );
}
